import {
  Copy,
  Database,
  Eye,
  GitCompare,
  Info,
  Lock,
  Star,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import React, { useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useBaseForecast } from '../../hooks/useBaseForecast';
import { useFormatting } from '../../hooks/useFormatting';
import { getForecastTerm } from '../../lib/terminology-config';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface BaseForecastCardProps {
  onView: () => void;
  onCompare: () => void;
  onClone: () => void;
}

export const BaseForecastCard: React.FC<BaseForecastCardProps> = ({
  onView,
  onCompare,
  onClone,
}) => {
  const { formatCurrency } = useFormatting();
  const { mode } = useApp();
  const [isFavourite, setIsFavourite] = useState(false);

  // Pull key metrics from shared base forecast context
  const { baseForecast: forecastData, isLoading } = useBaseForecast();
  const currentBalance = forecastData[0]?.balance ?? 0;
  const day90Balance = forecastData[forecastData.length - 1]?.balance ?? 0;
  const netChange = day90Balance - currentBalance;
  const minBalance = Math.min(...forecastData.map((d) => d.balance));

  const forecastTerm = getForecastTerm(true);

  if (isLoading) {
    return (
      <Card className="border-l-4 border-l-slate-700 bg-gradient-to-br from-slate-50 to-slate-100/50 shadow-md relative overflow-hidden">
        <CardContent className="relative p-6 flex items-center justify-center min-h-[280px]">
          <div className="flex flex-col items-center gap-3 text-slate-500">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-700" />
            <span className="text-sm">Loading base forecast…</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className="border-l-4 border-l-slate-700 bg-gradient-to-br from-slate-50 to-slate-100/50 shadow-md relative overflow-hidden">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03] bg-[repeating-linear-gradient(45deg,#000_0px,#000_1px,transparent_0px,transparent_50%)] bg-[length:10px_10px]" />

        <CardContent className="relative p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              {/* Badges row */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge className="bg-slate-700 text-white hover:bg-slate-700 font-medium">
                  <Lock className="h-3 w-3 mr-1" />
                  Base {forecastTerm}
                </Badge>
                <Badge
                  variant="outline"
                  className="bg-white/70 text-slate-600 border-slate-300 text-xs"
                >
                  <Database className="h-3 w-3 mr-1" />
                  {mode === 'demo' ? 'Access Financials' : 'Live Data'}
                </Badge>
                {isFavourite && (
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                )}
              </div>

              {/* Name */}
              <h3 className="text-xl font-semibold text-slate-900 mb-1">
                Base {forecastTerm}
              </h3>

              {/* Strapline */}
              <p className="text-sm text-slate-500 mb-3">
                Your ground truth — driven by connected data sources. Read-only.
              </p>
            </div>


          </div>

          {/* Key metric block */}
          <div className="mb-4 p-3 bg-white/70 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {netChange >= 0
                  ? <TrendingUp className="h-5 w-5 text-slate-600" />
                  : <TrendingDown className="h-5 w-5 text-slate-600" />}
                <span className="text-sm font-medium text-slate-700">
                  {netChange >= 0
                    ? `+${formatCurrency(netChange)} projected over 90 days`
                    : `${formatCurrency(netChange)} projected over 90 days`}
                </span>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-slate-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  Derived from your connected data sources. Edit your data in
                  Access Financials — this updates automatically.
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Metrics grid */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-xs text-slate-500 mb-1">Current Cash</div>
              <div className="text-sm font-semibold text-slate-900">
                {formatCurrency(currentBalance)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Day 90 Position</div>
              <div className="text-sm font-semibold text-slate-900">
                {formatCurrency(day90Balance)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Min Cash</div>
              <div
                className={cn(
                  'text-sm font-semibold',
                  minBalance < 50000
                    ? 'text-red-600'
                    : minBalance < 100000
                      ? 'text-amber-600'
                      : 'text-slate-900',
                )}
              >
                {formatCurrency(minBalance)}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-400 italic">
              Cannot be edited, parked, or deleted
            </p>
            <Badge
              variant="outline"
              className="text-xs bg-white/70 border-slate-300 text-slate-600"
            >
              Always Active
            </Badge>
          </div>

          {/* Quick action buttons */}
          <div className="mt-4 flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 bg-white/70 border-slate-300 text-slate-700 hover:bg-white"
              onClick={onView}
            >
              <Eye className="h-3 w-3 mr-1" />
              View
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 bg-white/70 border-slate-300 text-slate-700 hover:bg-white"
              onClick={onCompare}
            >
              <GitCompare className="h-3 w-3 mr-1" />
              Compare
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 bg-white/70 border-slate-300 text-slate-700 hover:bg-white"
              onClick={onClone}
            >
              <Copy className="h-3 w-3 mr-1" />
              Clone
            </Button>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
